import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ToastType = 'success' | 'error' | 'info';

/**
 * Acción opcional de un toast (botón tras el mensaje). `run` ejecuta la acción; el `toast-stack`
 * SIEMPRE descarta el toast tras pulsar. Sin `run` es un simple «Entendido»/«Cerrar».
 */
export interface ToastAction {
  label: string;
  run?: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  actions?: readonly ToastAction[];
}

export interface ShowToastInput {
  type: ToastType;
  message: string;
  /** Duración antes del auto-dismiss en ms. Default: 4000. `0` = persistente (solo se cierra a mano). */
  duration?: number;
  /** Botones de acción opcionales tras el mensaje (p. ej. «Ver destinos», «Entendido»). */
  actions?: readonly ToastAction[];
}

const DEFAULT_DURATION = 4000;

/**
 * Stack global de toasts. El `ToastStackComponent` (montado una única vez
 * en el root) lee `toasts()` y renderiza el stack. Los servicios y
 * componentes inyectan este servicio para disparar notificaciones.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  show({ type, message, duration = DEFAULT_DURATION, actions }: ShowToastInput): string {
    const id = this.generateId();
    this._toasts.update((list) => [...list, { id, type, message, actions }]);

    if (this.isBrowser && duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
    return id;
  }

  dismiss(id: string): void {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
